/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Pagination from 'react-js-pagination';
import SignalPopup from '../../heart-rate-sessions/SignalPopup';
import useTabData from './useTabData';
import { TabLoading, TabError, TabEmpty } from './TabStates';
import { q, num, Kpi } from './kit';

const LIMIT = 20;
const CHART_LIMIT = 1000;

// "YYYY-MM-DD HH:mm:ss" → "dd/MM/yyyy HH:mm:ss" (matches the heart-rate-sessions page).
const fmtDT = (s) => {
  if (!s || s === '-') return '-';
  const [d, t] = String(s).split(' ');
  if (!d) return '-';
  const [y, m, dd] = d.split('-');
  return `${dd}/${m}/${y}${t ? ` ${t}` : ''}`;
};

const perDayCounts = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    const day = String(r.timestamp_utc || r.start_time_stamp || '').slice(0, 10);
    if (!day) return;
    map.set(day, (map.get(day) || 0) + 1);
  });
  return Array.from(map, ([date, count]) => ({ date, count })).sort((a, b) => a.date.localeCompare(b.date));
};

// The "Alerts" tab (formerly Heart rate). Columns mirror the /heart-rate-sessions/:id page.
const HeartRateTab = ({ userId, from, to, authGet }) => {
  const [page, setPage] = useState(1);
  const [popup, setPopup] = useState(null);
  useEffect(() => { setPage(1); }, [userId, from, to]);

  // Day-wise counts: aggregate the window's sessions (does NOT change with page).
  const chart = useTabData(async () => {
    const res = await authGet(`/admin/api/heart-rate-sessions?${q(userId, from, to, { page: 1, limit: CHART_LIMIT })}`);
    return perDayCounts(res.data.data || []);
  }, [userId, from, to]);

  // Paginated session table + summary KPIs.
  const table = useTabData(async () => {
    const [summary, list] = await Promise.all([
      authGet(`/admin/api/heart-rate-sessions-summary?${q(userId, from, to)}`),
      authGet(`/admin/api/heart-rate-sessions?${q(userId, from, to, { page, limit: LIMIT })}`),
    ]);
    return { summary: summary.data.data || {}, rows: list.data.data || [], total: list.data.totalItem || 0 };
  }, [userId, from, to, page]);

  if (table.status === 'loading') return <TabLoading />;
  if (table.status === 'error') return <TabError onRetry={table.reload} />;

  const { summary, rows, total } = table.data;
  if (!rows.length && page === 1 && !(Number(summary.total_sessions) > 0)) {
    return <TabEmpty title="No alerts" message="This user has no alert (heart-rate) sessions in the selected window." icon="🔔" />;
  }
  const days = chart.data || [];

  return (
    <div data-testid="ud-alerts">
      <div className="ud-kpis ud-kpis--3">
        <Kpi label="Alerts" value={num(summary.total_sessions, 0)} />
        <Kpi label="Active days" value={num(summary.active_days, 0)} />
        <Kpi label="Avg max HR" value={num(summary.avg_max_heart_rate)} unit="bpm" />
      </div>

      <div className="au-panel">
        <div className="au-panel__head"><h4 className="au-panel__title">Alerts per day</h4></div>
        <div style={{ padding: '12px 8px' }}>
          {days.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={days} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f1" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#f26b45" name="Alerts" radius={[3, 3, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="au-chart-empty">No alerts to plot.</div>
          )}
        </div>
      </div>

      <div className="au-panel">
        <div className="au-panel__head"><h4 className="au-panel__title">Alert sessions</h4></div>
        <div className="au-table-wrap">
          <table className="au-table" data-testid="ud-alerts-table">
            <thead>
              <tr>
                <th>No</th><th>Name</th><th>Heart rate</th><th>Mood type</th><th>Step count</th>
                <th>Pause count</th><th>Glucose</th><th>Blood Pressure</th><th>Oxygen</th>
                <th>Tag</th><th>Location tag</th><th>Note</th><th>Start</th><th>End</th><th>Deleted</th><th>Signal Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id}>
                  <td data-label="No" className="tnum">{(page - 1) * LIMIT + i + 1}</td>
                  <td data-label="Name" className="au-ellipsis">{r.name}</td>
                  <td data-label="Heart rate" className="tnum">{r.min_heart_rate}-{r.max_heart_rate}</td>
                  <td data-label="Mood type">{r.mood_type}</td>
                  <td data-label="Step count" className="tnum">{r.step_count}</td>
                  <td data-label="Pause count" className="tnum">{r.pause_count}</td>
                  <td data-label="Glucose" className="tnum">{r.glucose}</td>
                  <td data-label="Blood Pressure">{r.blood_pressure}</td>
                  <td data-label="Oxygen" className="tnum">{r.oxygen}</td>
                  <td data-label="Tag">{r.tag_name}</td>
                  <td data-label="Location tag" className="au-ellipsis">{r.location_tag || '-'}</td>
                  <td data-label="Note" className="au-ellipsis">{r.note}</td>
                  <td data-label="Start">{fmtDT(r.start_time_stamp)}</td>
                  <td data-label="End">{fmtDT(r.end_time_stamp)}</td>
                  <td data-label="Deleted">{r.is_deleted === 'true' ? 'Yes' : 'No'}</td>
                  <td data-label="Signal Details">
                    <button
                      type="button"
                      className="au-link"
                      aria-label={`View signal details for record ${r.id}`}
                      onClick={() => setPopup(r.signal_hr_detail ?? null)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > LIMIT && (
          <div className="ud-pager" data-testid="ud-alerts-pager">
            <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={total} pageRangeDisplayed={5} onChange={setPage} />
          </div>
        )}
      </div>

      {popup != null && <SignalPopup data={popup} onClose={() => setPopup(null)} />}
    </div>
  );
};

export default HeartRateTab;
